import { Injectable, inject } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { DictionaryCollectionItem } from '../../shared/models/dictionary-collection-item';
import { CollectionRequestBuilder } from '@web/common';
import { Guid, once } from '@web/utils';
import { DictionaryDetailsForm } from '../../shared/models/dictionary-details-form';
import { DictionaryUpdateRequest } from '../../shared/api/requests/dictionary-update.request';
import {
  ConfirmationService,
  TableDataPayload,
  TableLazyLoad,
  ToastService,
} from '@web/shared/ui';
import { DictionaryCreateRequest } from '../../shared/api/requests/dictionary-create.request';
import { DictionaryCollectionFilter } from '../../shared/models/dictionary-collection-filter';
import { AdminDictionaryProductAttributeApiService } from '../api/services/admin-dictionary-product-attribute-api.service';

@Injectable({
  providedIn: 'root',
})
export class AdminDictionaryProductAttributeDataSourceService {
  /* prettier-ignore */
  private readonly adminDictionaryApi = inject(AdminDictionaryProductAttributeApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly confirmation = inject(ConfirmationService);
  private readonly toast = inject(ToastService);

  public fetchCollection(
    lazyLoad: TableLazyLoad<DictionaryCollectionFilter>,
  ): Observable<TableDataPayload<DictionaryCollectionItem>> {
    return this.adminDictionaryApi
      .fetchCollection(
        this.collectionRequestBuilder
          .page(lazyLoad.page)
          .pageSize(lazyLoad.rows)
          .sorts((builder) =>
            builder.fromTableSort(lazyLoad.sort).buildAsArray(),
          )
          .filters((builder) =>
            builder
              .fromTableFilter(lazyLoad.filter)
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('label').build(),
              )
              .appendDate((builder) => builder.rangeFor('createdAt').build())
              .buildAsArray(),
          )
          .build(),
      )
      .pipe(
        map(
          (response) =>
            new TableDataPayload(
              response.items.map(
                (item) =>
                  new DictionaryCollectionItem({
                    canBeEdited: item.contextOperations.canBeEdited,
                    canBeRemoved: item.contextOperations.canBeDeleted,
                    createdAt: item.createdAt,
                    id: item.id,
                    label: item.label,
                    position: item.position,
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  public fetchDetailsFormById(id: Guid): Observable<DictionaryDetailsForm> {
    return this.adminDictionaryApi.fetchDetailsById(id).pipe(
      map(
        (response) =>
          new DictionaryDetailsForm({
            label: response.label,
            position: response.position,
            slug: response.slug,
          }),
      ),
    );
  }

  public confirmAndRemoveById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz usunąć wybrany element?')
          .accept(() =>
            this.adminDictionaryApi
              .remove(id)
              .pipe(
                tap(() =>
                  this.toast.show((builder) =>
                    builder
                      .initSuccess('Wybrany element został pomyślnie usunięty.')
                      .build(),
                  ),
                ),
                once(),
              )
              .subscribe(subscriber),
          )
          .build(),
      );
    });
  }

  public remove(id: Guid): Observable<void> {
    return this.adminDictionaryApi.remove(id);
  }

  public updateByFormData(
    id: Guid,
    data: DictionaryDetailsForm,
  ): Observable<void> {
    const request = new DictionaryUpdateRequest({
      label: data.label,
      position: data.position,
    });

    return this.adminDictionaryApi.update(id, request);
  }

  public createByFormData(data: DictionaryDetailsForm): Observable<void> {
    const request = new DictionaryCreateRequest({
      label: data.label,
      position: data.position,
    });

    return this.adminDictionaryApi.create(request);
  }
}
